import React, {ChangeEvent, ReactElement, ReactNode, SyntheticEvent, useEffect, useMemo, useRef, useState} from 'react';
import {connect} from "react-redux";
import './CampaignCard.less';
import Auth from '@/Auth/Auth';
import Campaign, {CampaignHardCosts, CampaignRecurringCosts} from "@/entities/campaign";
import * as CampaignHelper from "./CampaignHelper";
import util from "@/lib/util";
import {hashIds} from "@/lib/memoize";
import OkCancel from "@/ui/Buttons/OkCancel";
import Loading from "@/ui/Loading";
import '@/stylesheets/form.less';
import Select from "@/ui/Select/Select";
import Checkbox, {CheckboxClickEvent} from "@/ui/Checkbox";
import ValidatedInput from "@/ui/ValidatedInput/ValidatedInput";
import * as DateUtil from "@/lib/dateutils";
import HardCosts from "../HardCosts/HardCosts";
import ContentBlockContainer from "@/ui/ContentBlock/ContentBlockContainer";
import ContentBlock from "@/ui/ContentBlock/ContentBlock";
import RecurringCosts from "../RecurringCosts/RecurringCosts";
import Icons from "@/ui/IconButton/Icons";
import Comments from "@/Comment/Comments";
import AssociatedTasks from "@/TaskManagement/AssociatedTasks/AssociatedTasks";
import DocumentBlock from "@/Documents/DocumentBlock";
import {confirmChangesMade, confirmWarningOkCancel} from "@/ui/Confirm";
import {deleteRelatedTasks, getUpdatedCost} from "@/TaskManagement/TaskHelper";
import {deleteTask} from "@/store/deps/taskDeps";

import EntityMeta from '@/ui/EntityMeta/EntityMeta';
import CasesBlock from '@/Case/CasesBlock/CasesBlock';
import HelpMessage from '@/i18n/HelpMessage';
import FormElement from '@/lib/FormElement';
import {getYear} from "date-fns";
import {formatUserName} from "@/Contacts/ContactFormat";
import {getCurrentFirmId, getCurrentUserId} from "@/lib/picos/currentLogin";
import {isMobileView, isTouchDevice} from "@/lib/picos/browserFns";
import {startEditingPage, stopEditingPage} from "@/lib/pageEditing";
import {deleteComment} from "@/store/deps/commentDeps";
import {RootState} from "@/@types/store";
import {EditableTableCardProps, EditableTablePropertyEntry, Justify} from "@/ui/EditableTable/EditableTable";
import {toTwoDecimals} from "@/lib/picos/numberFormat";
import Task from "@/entities/task";
import {uuid} from "@/@types/entity";
import {componentCacheStore} from "@/hooks/useComponentCache";
import useSubEditActive from "@/hooks/useSubEditActive";
import Category from "@/entities/category";
import ArrayMap from "@/lib/ArrayMap";
import ErrorBoundaryO from "@/ErrorBoundary/ErrorBoundaryO";

// Cache for getPresets
type PresetsResult = {
    categories: string[];
    impact: string;
    urgency: string;
    tieIns: { module: string; entity: string; id: string }[];
};
type PresetsCacheEntry = { result: PresetsResult; depsHash: string };
const presetsCache = new Map<string, PresetsCacheEntry>();

function computePresetsDepsHash(categories: Category[] | ArrayMap<Category>): string {
    return hashIds(categories.map(c => c.id));
}

type CampaignCardState = {
    current: Campaign,
    duplicate: boolean,
    hasChanged: boolean,
    isEditing: boolean,
    isSubmitting: boolean,
    isExpanded: boolean,
    okError: any | null, // if an error occurred when executing onOk()
    tasks: Array<any>
}

const periodOptions = [
    'days',
    'weeks',
    'months',
    'years'
].map(v => {
    return {label: v, value: v};
});

const CampaignCardInner: React.FC<CampaignCardProps> = (props) => {
    const initialState = useMemo<CampaignCardState>(() => ({
        current: props.current ? props.current.clone() : new Campaign({
            campaignId: props.newId,
            name: null,
            startDate: new Date(),
            periodLength: 1,
            periodUnit: 'months',
            usesPeriodFixedCost: false,
            category: 'Ad',
            cost: 0.0,
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: util.newHRTimestamp(),
            status: 'C'
        }),
        duplicate: false,
        hasChanged: false,
        isEditing: false,
        isExpanded: false,
        isSubmitting: false,
        okError: false,
        tasks: []
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);

    const cache = useMemo(
        () => componentCacheStore.typed<CampaignCardState>(`CampaignCard_${initialState.current.id}`),
        [initialState]
    );

    const [state, setState] = useState<CampaignCardState>(() => {
        if (cache.exists()) return {...initialState, ...cache.getItem()} as CampaignCardState;
        return initialState;
    });

    const setPartialState = (partial: Partial<CampaignCardState>) =>
        setState(prev => ({...prev, ...partial}));

    // Open-editor counter for inline sub-editors — centralised, never cached,
    // clamped (see useSubEditActive). Kept out of the cached `state` object so a
    // teardown-without-onEndEdit can't stick across remount.
    const {subEditActive, onBeginEdit, onEndEdit} = useSubEditActive();

    const doResetRef = useRef(false);
    const stateRef = useRef(state);
    stateRef.current = state;

    const categoriesList = useMemo(() => [
        'Ad',
        'Agencies',
        'Bar Association',
        'Blog',
        'Bulletins',
        'Charity and Community Events',
        'Chamber of Commerce',
        'CLE',
        'Establishment',
        'Event',
        'Personal Contact',
        'Leave Behind',
        'Newsletter',
        'Networking Group',
        'Partnership',
        'Periodical',
        'Podcast',
        'Presentation',
        'Search Engines',
        'Social Media',
        'Sponsorship',
        'Walk-In',
        'Website',
    ].sort()
        .map(v => {
            return {label: props.env?.getIconLabel(v), value: v};
        }),
    [props.env]);

    /**
     * Ensure that the recurring costs array contains entries for each period.
     *
     * Rebuilds the periods of the campaign being edited and returns it (OWA-980). The handlers pass the campaign
     * they just changed: rebuilding from state here would use the previous render's campaign and throw away the
     * user's change.
     */
    const computeRecurringCosts = (campaign: Campaign): Campaign => {
        const results = CampaignHelper.buildRecurringCosts(campaign, !!props.current);
        if (results) campaign.recurringCosts = results;

        return campaign;
    };

    // componentDidMount-equivalent
    useEffect(() => {
        setPartialState({current: computeRecurringCosts(stateRef.current.current.clone())});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // componentWillUnmount-equivalent
    useEffect(() => {
        return () => {
            if (doResetRef.current) cache.resetItem();
            else cache.setItem(stateRef.current);
            doResetRef.current = false;
        };
    }, [cache]);

    // componentDidUpdate equivalent: pick up external changes
    const prevStateRef = useRef(state);
    // Seeded from the revision the card shows (which a cache restore can leave
    // older than props), so a revision that arrived while unmounted is still handled.
    const lastHandledRevisionRef = useRef(state.current?.addedOn);
    useEffect(() => {
        const prevState = prevStateRef.current;
        if (props.current && (props.current.addedOn > state.current.addedOn) && props.current.addedOn !== lastHandledRevisionRef.current) {
            lastHandledRevisionRef.current = props.current.addedOn;
            const user = props.users.find(u => u.id === props.current.addedBy);
            const userName = formatUserName(user);

            if (!prevState.hasChanged) {
                const current = props.current.clone();
                setPartialState({current, hasChanged: false});
            }
            else {
                confirmWarningOkCancel(
                    'External Changes Detected',
                    (
                        <div>
                            <p><strong>{userName}</strong> has made changes to this campaign. Do you want to apply their
                                changes, or ignore them?</p>
                            <p>If you apply their changes, you will lose any changes you have made. If you ignore their
                                changes and save your own, you will overwrite their changes.</p>
                        </div>
                    ),
                    'Apply Changes', 'Ignore',
                    () => {
                        const current = props.current.clone();
                        setPartialState({current, hasChanged: false});
                    },
                    () => {
                    }
                );
            }
        }
        prevStateRef.current = state;
    });

    const selfRef = {props, state: {current: state.current}} as any;

    const okErrorConfirmed = () => {
        setPartialState({okError: null});
    };

    const onBeforeExpand = (_title: string) => {
        setPartialState({isExpanded: true});
    };

    const onBeforeCollapse = () => {
        setPartialState({isExpanded: false});
    };

    const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();

        const name = e.target.value === '' ? null : e.target.value;

        const campaign = state.current.clone();

        if (props.campaigns.find(c => c.name === name)) {
            campaign.name = name;

            if (state.current.name === name) setPartialState({current: campaign, duplicate: false});
            else setPartialState({current: campaign, duplicate: true});
        }
        else {
            campaign.name = name;

            startEditingPage(selfRef, 'campaign');
            setPartialState({current: campaign, duplicate: false, hasChanged: true});
        }
    };

    const handleCategoryChange = (option: uuid) => {
        const campaign = state.current.clone();
        campaign.category = option;

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: campaign, hasChanged: true});
    };

    const handleWebsiteChange = (e: ChangeEvent<HTMLInputElement>) => {
        const campaign = state.current.clone();
        campaign.website = e.target.value;

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: campaign, hasChanged: true});
    };

    /** The period length to save in place of one that is not a whole number of at least 1: the stored one, or 1 (OWA-980). */
    const storedPeriodLength = (): number =>
        CampaignHelper.isValidPeriodLength(props.current?.periodLength) ? props.current.periodLength : 1;

    const handleUsesPeriodChange: CheckboxClickEvent = () => {
        const campaign = state.current.clone();
        campaign.usesPeriod = !state.current.usesPeriod;

        // The period length is hidden once the campaign stops recurring, so a cleared or negative one must not block Save
        // with a message about a field the firm cannot see (OWA-980). Put back the stored one.
        if (!campaign.usesPeriod && !CampaignHelper.isValidPeriodLength(campaign.periodLength)) {
            campaign.periodLength = storedPeriodLength();
        }

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: computeRecurringCosts(campaign), hasChanged: true});
    };

    const handlePeriodLengthChange = (e: ChangeEvent<HTMLInputElement>) => {
        const campaign = state.current.clone();
        campaign.periodLength = Math.floor(Number(e.target.value ?? 0));

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: computeRecurringCosts(campaign), hasChanged: true});
    };

    const handlePeriodUnitChange = (option: uuid) => {
        const campaign = state.current.clone();
        campaign.periodUnit = option;

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: computeRecurringCosts(campaign), hasChanged: true});
    };

    const handleUsesPeriodFixedCost: CheckboxClickEvent = () => {
        const campaign = state.current.clone();
        campaign.usesPeriodFixedCost = !campaign.usesPeriodFixedCost;

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: computeRecurringCosts(campaign), hasChanged: true});
    };

    const handlePeriodFixedCost = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === '') return;

        const campaign = state.current.clone();
        campaign.periodFixedCost = toTwoDecimals(Number.parseFloat(e.target.value));

        // New row objects: the rows belong to the previous state, and can be the stored campaign's own (OWA-980)
        campaign.recurringCosts = campaign.recurringCosts.map(c => c.cost ? c : {...c, cost: campaign.periodFixedCost});

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: computeRecurringCosts(campaign), hasChanged: true});
    };

    const handleStartDateChange = (value: string) => {
        const startDate = DateUtil.parseDate(value);

        if (getYear(startDate) < 1000) return;

        const campaign = state.current.clone();
        campaign.startDate = startDate;

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: computeRecurringCosts(campaign), hasChanged: true});
    };


    const handleEndDateChange = (value: string) => {
        const endDate = DateUtil.parseDate(value);

        if (getYear(endDate) < 1000) return;

        const campaign = state.current.clone();
        campaign.endDate = endDate;

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: computeRecurringCosts(campaign), hasChanged: true});
    };

    const handleHardCostsChange = (hardCosts: CampaignHardCosts[]) => {
        const campaign = state.current.clone();
        campaign.hardCosts = [...hardCosts];

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: campaign, hasChanged: true});
    };

    const handleRecurringCostsChange = (recurringCosts: CampaignRecurringCosts[]) => {
        const campaign = state.current.clone();
        campaign.recurringCosts = [...recurringCosts];

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: campaign, hasChanged: true});
    };

    /**
     * Update current with changed field value.
     */
    const handleTextChange = (e: ChangeEvent<HTMLInputElement>, field: string) => {
        e.preventDefault();

        const campaignClone = state.current.clone();
        campaignClone[field] = e.target.value;

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: campaignClone, hasChanged: true});
    };

    const isEndDateValid = (dateString: string) => {
        const dt = DateUtil.parseDate(dateString)?.getTime() ?? 0;
        return dt > (state.current.startDate?.getTime() ?? 0);
    };

    const onClosedClick = () => {
        const campaign = state.current.clone();
        campaign.closed = !campaign.closed;

        startEditingPage(selfRef, 'campaign');
        setPartialState({current: campaign, hasChanged: true});
    };

    // onBeginEdit / onEndEdit come from useSubEditActive (see above).

    const onCancel = (e: SyntheticEvent) => {
        e.preventDefault();

        const _doWork = () => {
            doResetRef.current = true;

            if (!props.current) deleteRelatedTasks('Marketing', 'Campaign', state.current.id);

            stopEditingPage(selfRef, 'campaign');

            if (!props.current) {
                const comments = props.comments.getMappingFor('entityId', state.current.id);
                comments.forEach(c => deleteComment(c));
            }

            state.tasks.forEach(t => {
                deleteTask(t);
            });

            props.onCancel();
        };

        if (state.hasChanged) {
            confirmChangesMade(_doWork);
        }
        else {
            _doWork();
        }

    };

    const onOk = (e: SyntheticEvent) => {
        e.preventDefault();

        doResetRef.current = true;

        setPartialState({isSubmitting: true});
        try {
            const campaign = state.current.clone();

            campaign.addedOn = util.newHRTimestamp();
            campaign.addedBy = getCurrentUserId();
            campaign.status = 'U';

            campaign.hardCosts = state.current.hardCosts.filter(c => c.item !== '' && c.cost !== 0);
            campaign.recurringCosts = [...state.current.recurringCosts];

            // Never save a period length that is not a whole number of at least 1 (OWA-980). Save is disabled for a
            // recurring campaign until it is fixed; one that is not recurring does not use it, so put back the stored one.
            if (!CampaignHelper.isValidPeriodLength(campaign.periodLength)) {
                campaign.periodLength = storedPeriodLength();
            }

            stopEditingPage(selfRef, 'campaign');

            props.onOk(campaign);

            setPartialState({isSubmitting: false});
        }
        catch (err) {
            console.error('CampaignCard::onOk::this.props.onOk returned', err);
            setPartialState({okError: err, isSubmitting: false});
        }
    };

    const onTaskAdded = (task: Task) => {
        const tasks = [...state.tasks];
        tasks.push(task);

        setPartialState({tasks});
    };

    const formatHardCosts = (v: CampaignHardCosts, _before: CampaignHardCosts[], _after: CampaignHardCosts[]): string | null => {
        if (v) {
            return `${v.item} for $${v.cost} on ${v.incurredOn}`;
        }
        else return null;
    };

    const formatRecurringCosts = (v: CampaignRecurringCosts, _before: CampaignRecurringCosts[], _after: CampaignRecurringCosts[]): string | null => {
        if (v) {
            return `${v.startDate} - ${v.endDate} for $${v.cost}`;
        }
        else return null;
    };

    const renderElement = (label: string, uiLabel: string, element: ReactNode) => {
        return (
            <div className="Row">
                <div className={`FormComponent campaign-card__${label}`}>
                    <label htmlFor={label} className="FormComponentLabel">{uiLabel}</label>
                    <div className="FormComponentValue">
                        {element}
                    </div>
                </div>
            </div>
        );
    };

    const renderProperty = (task: Task, field: string, isExport: boolean): string | number | ReactElement => {
        const split = task.tieIns.filter(ti => ti.module === 'Marketing' && ti.entity === 'Campaign').length;

        if (field === 'split') return split;

        const hours = task.elapsedTime > 0 ? task.elapsedTime / 3600000 : task.duration;

        if (field === 'time') return util.toDecimals(hours, 1);

        const billingRate = task.associatedRate;

        if (field === 'rate' && isExport) return billingRate;

        const rate = billingRate.toFixed(2);

        if (field === 'rate') return util.formatCurrency(Number(rate));

        const _totalCost = getUpdatedCost(task, true, 'simple');
        const totalCost = util.numberWithCommas(_totalCost);

        if (field === 'cost') return totalCost;

        else return isExport ? totalCost : util.formatCurrency(_totalCost);
    };

    const getPresets = (campaign: Campaign): PresetsResult => {
        const cacheKey = campaign.id;
        const depsHash = computePresetsDepsHash(props.categories);

        const cached = presetsCache.get(cacheKey);
        if (cached && cached.depsHash === depsHash) {
            if (cached.result.tieIns[0]?.id !== campaign.id) {
                cached.result.tieIns[0].id = campaign.id;
            }
            return cached.result;
        }

        const soleHit = props.categories
            .filter(c => c.relatedAreas.includes('Marketing') && c.status !== 'D' && c.relatedAreas.length === 1)
            .map(cat => cat.id);

        const categories = soleHit.length > 0 ? soleHit : props.categories
            .filter(c => c.relatedAreas.includes('Marketing') && c.status !== 'D')
            .map(cat => cat.id);

        const result: PresetsResult = {
            categories,
            impact: 'M',
            urgency: 'M',
            tieIns: [
                {
                    module: 'Marketing',
                    entity: 'Campaign',
                    id: campaign.id
                }
            ]
        };

        presetsCache.set(cacheKey, {result, depsHash});
        return result;
    };

    const tableProperties: EditableTablePropertyEntry<Task>[] = [
        {
            label: 'time',
            renderItemFn: renderProperty,
            width: 'max-content',
            hideOnMobile: true,
            justify: Justify.RIGHT,
            type: 'number'
        },
        {
            label: 'split',
            renderItemFn: renderProperty,
            width: 'max-content',
            hideOnMobile: true,
            justify: Justify.CENTER,
            type: 'number'
        },
        {
            label: 'cost',
            renderItemFn: renderProperty,
            width: 'max-content',
            justify: Justify.RIGHT,
            type: 'number'
        }
    ];

    const commentTitle = <HelpMessage module='comment' field='title'/>;
    const documentTitle = <HelpMessage module='document' field='title'/>;
    const laborTitle = <HelpMessage module='task' field='laborCosts'/>;

    const userId = getCurrentUserId();
    const authorized = Auth.hasRole(userId, 'Marketing Manager') || Auth.isAdmin(userId);

    // For some reason this is now being called from ContactCard when saving.  Must be something weird in the
    // new React version.  In either case, this is a workaround to handle being called improperly.
    if (!props.env) {
        console.debug('Error, no env passed');
        return (
            <div>Error, no environment passed</div>
        );
    }

    const campaign = state.current;

    const element = new FormElement('campaign-card', campaign, handleTextChange);

    // A recurring campaign needs a whole number of periods to save (OWA-980); handleUsesPeriodChange and onOk keep one
    // that is not recurring valid.
    const periodLengthInvalid = campaign.usesPeriod && !CampaignHelper.isValidPeriodLength(campaign.periodLength);
    const periodLengthMessage = 'Enter a whole number of 1 or more.';

    const disabled = (
        subEditActive > 0 || // Refuse save while an inline sub-editor is open (protects its draft)
        state.isSubmitting ||
        !state.hasChanged ||
        state.duplicate ||
        periodLengthInvalid ||
        !campaign.isValid()
    );

    const disabledMessage = (subEditActive > 0 || disabled)
                            ? subEditActive > 0
                              ? `${subEditActive} Edit${subEditActive > 1 ? 's' : ''} In Progress`

                              : periodLengthInvalid
                                ? periodLengthMessage

                              : !campaign.isValid()
                                ? campaign.validationError

                                : state.duplicate
                                  ? 'Already a campaign with this name'

                                  : null
                            : null;

    const dateFormat = 'MM/DD/YYYY';
    const startDate = campaign.startDate ? DateUtil.toMoment(campaign.startDate).format(dateFormat) : '';
    const endDate = campaign.endDate ? DateUtil.toMoment(campaign.endDate).format(dateFormat) : '';
    // Rows of a campaign that is not recurring are neither counted nor rebuilt (CampaignHelper), so they lock nothing (OWA-980)
    const recurringCostsDisabled = props.current && campaign.usesPeriod && state.current.recurringCosts.some(c => c.cost > 0);

    const periodUnit = campaign.periodLength > 1 ? campaign.periodUnit : campaign.periodUnit.substring(0, campaign.periodUnit.length - 1);
    const periodLength = campaign.periodLength > 1 ? campaign.periodLength + ' ' : '';
    const isCostTheSame = `Is the cost the same every ${periodLength}${periodUnit}?`;

    const hardCosts = CampaignHelper.getHardCosts(campaign);
    const laborCosts = CampaignHelper.getLaborCosts(campaign, props.tasks.getMappingFor('campaignId', state.current.id), false) as number;
    const recurringCosts = CampaignHelper.getRecurringCosts(campaign, false) as number;
    const totalCosts = CampaignHelper.getTotalCost(campaign, props.tasks.getMappingFor('campaignId', state.current.id), false) as number;

    let errorMessage = '';
    if (state.okError) {
        if (state.okError.message) {
            if (state.okError.message.clientMessage) errorMessage = state.okError.message.clientMessage;
            else errorMessage = state.okError.message;
        }
    }

    const allowDateChanges = !props.current
        || !campaign.usesPeriod
        || campaign.recurringCosts?.length === 0
        || campaign.recurringCosts?.every(rc => !Number(rc.cost)); // Amounts can be stored as text, "0.00" (OWA-980)

    if (state.isSubmitting) return (
        <div className="campaign-card">
            <Loading/>
        </div>
    );
    else return (
        <div className="campaign-card">

            {/*Form fields */}
            <div className="main-content">
                <div className="Form">
                    <div className='campaign-card__header'>
                        {/* Total */}
                        <div className='campaign-card__total-cost'>
                            This campaign has cost {util.formatCurrency(totalCosts, false)}
                            &nbsp;({util.formatCurrency(CampaignHelper.getCostPerLead(campaign), false)}&nbsp;/ lead
                            ).
                        </div>
                        <div className='campaign-card__total-cost'>
                            The conversion rate is <span
                            className='u-percent'>{`${CampaignHelper.getConversionRate(campaign)}%`}</span>.
                        </div>
                    </div>

                    {/* Campaign Name */}
                    {element.renderInputOptions({
                        paramName: 'name',
                        uiLabel: 'Name',
                        autoFocus: !isTouchDevice(),
                        required: true,
                        type: 'text',
                        placeholder: 'My New Campaign'
                    })}

                    {/* Category */}
                    {element.renderElement('category', 'Category',
                        <Select name="category"
                                allowNone={false}
                                options={categoriesList}
                                defaultValue={{
                                    label: props.env?.getIconLabel(campaign.category),
                                    value: campaign.category
                                }}
                                onChange={handleCategoryChange}
                                required={true}
                        />, true
                    )}

                    {/* Website */}
                    {element.renderInput('website', 'Website', "text", false, 'https://mywebsite.com')}

                    {/* Date Range */}
                    {renderElement('dateRange', 'Date Range', (
                        <React.Fragment>
                            <ValidatedInput type='date'
                                            defaultValue={startDate}
                                            onValid={handleStartDateChange}
                                            disabled={!allowDateChanges}
                            />
                            <span>to</span>
                            <ValidatedInput type='date'
                                            defaultValue={endDate}
                                            onValid={handleEndDateChange}
                                            validatorFunc={isEndDateValid}
                                            required={false}
                                            showErrorMessage={false}
                            />
                            <span
                                className='campaign-card__help'>Leave end date blank for auto-renewing campaigns</span>
                        </React.Fragment>
                    ))}

                    {/* Closed? */}
                    {element.renderCheckbox('closed', 'Closed?', false, onClosedClick)}

                    {/* Recurring? */}
                    {renderElement('period', 'Recurring', (
                        <React.Fragment>
                            <Checkbox checked={campaign.usesPeriod}
                                      label='Is this a recurring campaign?'
                                      onClick={handleUsesPeriodChange}
                                      disabled={recurringCostsDisabled}
                            />

                            {recurringCostsDisabled &&
								<div className='campaign-card__help'>
									<div>Can not change period when existing costs have
										been entered into Recurring Costs. Please start a new campaign if the period
										changes.
									</div>
									<div>If this is a new campaign, you can also set all of the recurring costs to
										0.00.
									</div>
								</div>
                            }

                            {campaign.usesPeriod &&
								<div className='campaign-card__uses-period'>
									<div className='campaign-card__period-how-often'>How often does this campaign
										repeat?
									</div>

									<div className='campaign-card__period-info-every'>
										<span className='campaign-card__period-info-every-label'>Every</span>
										<input type='number'
										       onChange={handlePeriodLengthChange}
										       min="1"
										       step="1"
										       disabled={recurringCostsDisabled}
										       value={campaign.periodLength}
										/>
										<Select name='period-options'
                                                options={periodOptions}
										        defaultValue={
                                                    periodOptions
                                                        .find(p => p.label === campaign.periodUnit) || periodOptions[2]}
										        onChange={handlePeriodUnitChange}
										        disabled={recurringCostsDisabled}
										/>
									</div>
                                    {periodLengthInvalid &&
										<div className='campaign-card__help'>{periodLengthMessage}</div>
                                    }

                                    {/* Fixed costs for recurring campaigns */}
									<div className='campaign-card__uses-period-fixed-cost'>
										<Checkbox checked={campaign.usesPeriodFixedCost}
										          label={isCostTheSame}
										          onClick={handleUsesPeriodFixedCost}
										          disabled={props.current?.usesPeriodFixedCost}
										/>

                                        {campaign.usesPeriodFixedCost &&
											<div className='campaign-card__period-fixed-cost'>
												<div className="dollar-sign">$</div>
												<input className='money'
												       type='number'
												       onBlur={handlePeriodFixedCost}
												       min="1"
												       step=".01"
												       defaultValue={campaign.periodFixedCost}
												       disabled={props.current?.usesPeriodFixedCost}
												/>
											</div>
                                        }
									</div>
								</div>
                            }
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* History */}
            <EntityMeta entityType="campaign"
                        entity={campaign}
                        hidden={!!state.isExpanded}
                        onVersionChange={(updated) => setPartialState({current: updated as Campaign})}
            />

            {/* Containers for types of costs, notes and comments */}
            <ContentBlockContainer name={`cbc-campaign-card-${campaign.id}`}
                                   displayName={`cbc-campaign-card`}
                                   breakWidth={500}
                                   onBeforeExpand={onBeforeExpand}
                                   onBeforeCollapse={onBeforeCollapse}
            >

                {/* Tasks */}
                {state.current.name &&
					<ContentBlock key={`cb-campaign-tasks-${campaign.id}`}
					              title={`cb-campaign-tasks-${campaign.id}`}
					              displayTitle={laborTitle}
					              disabled={!authorized}
					              icon={Icons.TASKS.reactIcon}
					              customStyles='container--tasks'
					              autoScroll={false}
					              message={util.formatCurrency(laborCosts)}
					>
						<AssociatedTasks topLevel='campaign'
						                 topLevelId={campaign.id}
						                 onBeforeEdit={onBeginEdit}
						                 onEndEdit={onEndEdit}
						                 exportTitle='Campaign Time Entries'
						                 presets={getPresets(campaign)}
						                 properties={tableProperties}
						                 unsavedEntity={campaign}
						                 showClientNames={false}
						                 showCompletionDate={false}
						                 showTimer={!isMobileView()}
						                 showPriority={!isMobileView()}
						                 showInvoice={false}
						                 showToggles={true}
						                 showDueDate={true}
						/>
					</ContentBlock>}

                {/* Recurring Costs */}
                <ContentBlock key={`cb-recurring-costs-${campaign.id}`}
                              title={`cb-recurring-costs-${campaign.id}`}
                              displayTitle='Recurring Costs'
                              customStyles='container--recurring-costs'
                              disabled={!authorized}
                              message={util.formatCurrency(recurringCosts)}
                              icon={Icons.RECURRING.reactIcon}
                              isHiddenOverride={!campaign.usesPeriod}
                              autoScroll={true}
                >
                    <RecurringCosts costs={campaign.recurringCosts} onChange={handleRecurringCostsChange}/>
                </ContentBlock>

                {/* Fixed Costs */}
                <ContentBlock key={`cb-hard-costs-${campaign.id}`}
                              title={`cb-hard-costs-${campaign.id}`}
                              displayTitle='Hard Costs'
                              customStyles='container--hard-costs'
                              disabled={!authorized}
                              message={util.formatCurrency(hardCosts)}
                              icon={Icons.CHECK.reactIcon}
                              autoScroll={true}
                              onEditCompleted={handleHardCostsChange}
                >
                    <HardCosts campaign={campaign}
                               items={campaign.hardCosts}
                               onChange={handleHardCostsChange}
                    />
                </ContentBlock>

                {/* Leads and Cases */}
                <ContentBlock key={`cb-campaign-related-leads-cases-${campaign.id}`}
                              title={`cb-campaign-related-leads-cases-${campaign.id}`}
                              displayTitle="Leads and Cases"
                              icon={Icons.CASE.reactIcon}
                              customStyles='container--leads-and-cases'
                              hasContent={true}
                              autoScroll={false}
                >
                    <CasesBlock matters={CampaignHelper.getCases(campaign)}
                                leads={CampaignHelper.getLeads(campaign)}
                    />
                </ContentBlock>

                {/* Notes and Documents (replaces attachments) */}
                <ContentBlock key={`cb-campaign-documents-${campaign.id}`}
                              title={`cb-campaign-documents-${campaign.id}`}
                              displayTitle={documentTitle}
                              disabled={!authorized}
                              customStyles='container--documents'
                              color='black'
                              hasContent={true}
                              icon={Icons.FILE_TEXT.reactIcon}
                              autoScroll={false}
                >
                    <DocumentBlock entityName='campaign'
                                   entityId={campaign.id}
                                   onBeforeEdit={onBeginEdit}
                                   onEndEdit={onEndEdit}
                    />
                </ContentBlock>

                {/* Comments */}
                <ContentBlock key={`cb-campaign-private-discussion-${campaign.id}`}
                              title={`cb-campaign-private-discussion-${campaign.id}`}
                              displayTitle={commentTitle}
                              icon={Icons.COMMENTS.reactIcon}
                              customStyles='container--comments'
                              hasContent={true}
                >
                    <Comments entityId={campaign.id}
                              entityType='campaign'
                              isEditing={true}
                    />
                </ContentBlock>

            </ContentBlockContainer>

            {/* Button bar */}
            <OkCancel label='Campaign'
                      disabled={!!disabled}
                      disabledMessage={disabledMessage}
                      useDisabledMessage={subEditActive > 0}
                      onClick={onOk}
                      onCancel={onCancel}
                      onTop={true}
            />

            {state.okError && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <strong>Error: {errorMessage}</strong>
                    <button type="button"
                            className="close" data-dismiss="alert"
                            onClick={okErrorConfirmed}
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            )}
        </div>
    );
};

const CampaignCard: React.FC<CampaignCardProps> = (props) => (
    <ErrorBoundaryO uniqueKey={`campaign-card-${props.current?.id || props.newId}`} shouldUseErrorFrame={true}>
        <CampaignCardInner {...props}/>
    </ErrorBoundaryO>
);

export type CampaignCardProps = ReturnType<typeof mapStateToProps> & EditableTableCardProps<Campaign>;

const mapStateToProps = (state: RootState) => {
    return {
        campaigns: state.campaigns,
        categories: state.categories,
        comments: state.comments,
        tasks: state.tasks,
        users: state.users
    };
};

export default connect(mapStateToProps)(CampaignCard);
